export interface LocationData {
  latitude: number;
  longitude: number;
  country: string;
  city: string;
  timezone: string;
  accuracy?: number;
}

export interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

class GeolocationService {
  private cachedLocation: LocationData | null = null;
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  /**
   * Get user's current location with caching
   */
  async getCurrentLocation(options: GeolocationOptions = {}): Promise<LocationData> {
    const now = Date.now();
    
    // Return cached location if still valid
    if (this.cachedLocation && now < this.cacheExpiry) {
      console.log('📍 Using cached location:', this.cachedLocation);
      return this.cachedLocation;
    }

    try {
      // Try browser geolocation first
      const location = await this.getBrowserLocation(options);
      this.cacheLocation(location);
      return location;
    } catch (error) {
      console.warn('⚠️ Browser geolocation failed, trying IP-based fallback:', error);
      
      try {
        // Fallback to IP-based geolocation
        const location = await this.getIPBasedLocation();
        this.cacheLocation(location);
        return location;
      } catch (fallbackError) {
        console.error('❌ All geolocation methods failed:', fallbackError);
        
        // Final fallback to default values
        const defaultLocation = this.getDefaultLocation();
        this.cacheLocation(defaultLocation);
        return defaultLocation;
      }
    }
  }

  /**
   * Get location using browser's geolocation API
   */
  private getBrowserLocation(options: GeolocationOptions): Promise<LocationData> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      const geolocationOptions: PositionOptions = {
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        timeout: options.timeout ?? 10000,
        maximumAge: options.maximumAge ?? 5 * 60 * 1000 // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude, accuracy } = position.coords;
            const location = await this.reverseGeocode(latitude, longitude);
            resolve({
              latitude,
              longitude,
              country: location.country,
              city: location.city,
              timezone: location.timezone,
              accuracy
            });
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          reject(new Error(`Geolocation error: ${error.message}`));
        },
        geolocationOptions
      );
    });
  }

  /**
   * Get location using IP-based geolocation as fallback
   */
  private async getIPBasedLocation(): Promise<LocationData> {
    try {
      // Use ipapi.co for IP-based geolocation (free tier)
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        throw new Error(`IP geolocation failed: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        latitude: data.latitude || 0,
        longitude: data.longitude || 0,
        country: data.country_code || 'Unknown',
        city: data.city || 'Unknown',
        timezone: data.timezone || 'UTC'
      };
    } catch (error) {
      throw new Error(`IP geolocation failed: ${error}`);
    }
  }

  /**
   * Reverse geocode coordinates to get country and city
   * Tries Google Maps Geocoder first, then falls back to IP-based geolocation
   */
  private async reverseGeocode(latitude: number, longitude: number): Promise<{ country: string; city: string; timezone: string }> {
    // Try Google Maps Geocoder if available
    if (window.google?.maps?.Geocoder) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        return new Promise((resolve, reject) => {
          geocoder.geocode(
            { location: { lat: latitude, lng: longitude } },
            (results, status) => {
              if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
                const result = results[0];
                let country = 'Unknown';
                let city = 'Unknown';
                
                // Extract country and city from address components
                for (const component of result.address_components || []) {
                  if (component.types.includes('country')) {
                    country = component.short_name || component.long_name || 'Unknown';
                  }
                  if (component.types.includes('locality') || component.types.includes('administrative_area_level_1')) {
                    city = component.long_name || component.short_name || 'Unknown';
                  }
                }
                
                // Use browser timezone if available, otherwise estimate from coordinates
                const timezone = this.getUserTimezone() || this.getTimezoneFromCoordinates(latitude, longitude);
                
                resolve({ country, city, timezone });
              } else {
                // Google Maps failed, try IP-based fallback
                this.getIPBasedLocation()
                  .then(ipLocation => resolve({
                    country: ipLocation.country,
                    city: ipLocation.city,
                    timezone: ipLocation.timezone
                  }))
                  .catch(() => {
                    // Final fallback
                    resolve({
                      country: 'Unknown',
                      city: 'Unknown',
                      timezone: this.getTimezoneFromCoordinates(latitude, longitude)
                    });
                  });
              }
            }
          );
        });
      } catch (error) {
        console.warn('⚠️ Google Maps Geocoder error, trying IP fallback:', error);
        // Fall through to IP-based fallback
      }
    }
    
    // Fallback to IP-based geolocation if Google Maps not available
    try {
      const ipLocation = await this.getIPBasedLocation();
      return {
        country: ipLocation.country,
        city: ipLocation.city,
        timezone: ipLocation.timezone
      };
    } catch (error) {
      console.warn('⚠️ IP-based geolocation failed, using defaults:', error);
      return {
        country: 'Unknown',
        city: 'Unknown',
        timezone: this.getTimezoneFromCoordinates(latitude, longitude)
      };
    }
  }

  /**
   * Estimate timezone from coordinates
   */
  private getTimezoneFromCoordinates(latitude: number, longitude: number): string {
    // Simple timezone estimation based on longitude
    // This is a rough approximation - in production you'd use a proper timezone database
    const hourOffset = Math.round(longitude / 15);
    const utcOffset = hourOffset >= 0 ? `+${hourOffset.toString().padStart(2, '0')}:00` : `${hourOffset.toString().padStart(2, '0')}:00`;
    
    return `UTC${utcOffset}`;
  }

  /**
   * Get default location when all geolocation methods fail
   */
  private getDefaultLocation(): LocationData {
    return {
      latitude: 56.9496, // Riga, Latvia coordinates
      longitude: 24.1052,
      country: 'LV',
      city: 'Riga',
      timezone: 'Europe/Riga'
    };
  }

  /**
   * Cache location data with expiry
   */
  private cacheLocation(location: LocationData): void {
    this.cachedLocation = location;
    this.cacheExpiry = Date.now() + this.CACHE_DURATION;
    console.log('📍 Cached new location:', location);
  }

  /**
   * Clear cached location
   */
  clearCache(): void {
    this.cachedLocation = null;
    this.cacheExpiry = 0;
    console.log('📍 Location cache cleared');
  }

  /**
   * Get cached location if available
   */
  getCachedLocation(): LocationData | null {
    if (this.cachedLocation && Date.now() < this.cacheExpiry) {
      return this.cachedLocation;
    }
    return null;
  }

  /**
   * Check if geolocation is supported
   */
  isSupported(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Get user's timezone
   */
  getUserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.warn('⚠️ Could not detect timezone, using UTC');
      return 'UTC';
    }
  }
}

// Export singleton instance
export const geolocationService = new GeolocationService();

// Export utility functions
export const getCurrentLocation = (options?: GeolocationOptions) => geolocationService.getCurrentLocation(options);
export const getUserTimezone = () => geolocationService.getUserTimezone();
export const isGeolocationSupported = () => geolocationService.isSupported();
